/**
 * Shared Atlas runtime state.
 *
 * A small mutable context the Atlas modules read and write. Modules stay
 * decoupled by signalling through `app.events` (the EngineX EventBus); this
 * object holds the references they all need (connection, local identity, the
 * world they're in, the avatar registry). It is intentionally a plain singleton
 * for the P0 slice — later milestones can promote pieces into proper ECS
 * components.
 */

import type * as THREE from 'three/webgpu';
import type { DbConnection } from './module_bindings/index.js';
import type { AvatarAppearance } from '@shared/avatar.js';
import type { Asset, InventoryItem } from '@shared/types/inventory.js';
import { IntentQueue } from './lib/intent_queue.js';
import type {
  PulseState,
  CompassState,
  OrbitState,
  OrbitMemberState,
  NeedSatisfier,
} from '@shared/types/social.js';
import type {
  ReputationScores,
  VouchEdge,
  Review,
  BetrayalRecord,
} from '@shared/types/reputation.js';
import type {
  EventSpec,
  EventAttendance,
  RitualState,
  RitualMute,
  NpcHost,
  MemoryEntry,
} from '@shared/types/events.js';
import type {
  Group,
  GroupMember,
  GroupSignature,
  CharterChallenge,
  TreasurySpend,
  SpendVote,
  ForkVote,
  ForkBallot,
} from '@shared/types/groups.js';

/** The local player's portable avatar + the server-authoritative complexity. */
export interface LocalAvatar {
  appearance: AvatarAppearance;
  complexity: number;
}

/** One buffered authoritative transform sample (client-clock arrival time). */
export interface RemoteSnap {
  t: number;
  x: number;
  z: number;
  yaw: number;
}

/** A remote avatar's render proxy + interpolation target. */
export interface RemoteAvatar {
  identityHex: string;
  /** Render proxy, created lazily by the avatar module from `appearance`. */
  mesh: THREE.Object3D | null;
  /** Portable identity appearance (from the global `avatar` table). */
  appearance: AvatarAppearance | null;
  /** Server-authoritative render-cost score. */
  complexity: number;
  /** Interpolation target (latest authoritative transform). */
  targetX: number;
  targetZ: number;
  targetYaw: number;
  /** Snapshot ring for buffered interpolation (render ~2× the update interval in the past). */
  snaps: RemoteSnap[];
  /** EMA of the observed authoritative update interval (ms) — sizes the interp delay. */
  snapIntervalMs: number;
  /** Authoritative anim state ('ride:<key>' / 'fly:<chassis>' mark a vehicle). */
  animState: string;
  /** Authoritative altitude — only meaningful while the remote flies. */
  targetY: number;
}

/** A placed world object's data (the world module owns its mesh). */
export interface PlacedObject {
  objectId: bigint;
  prefabId: string;
  x: number;
  y: number;
  z: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  rotW: number;
  scale: number;
  mesh: THREE.Object3D | null;
}

/** A live marketplace listing (mirror of the server `market_listing` row). */
export interface MarketListing {
  listingId: bigint;
  /** Seller identity hex. */
  seller: string;
  assetId: bigint;
  /** Fixed price in Sovereigns (base units). */
  priceSov: bigint;
  /** Micros-since-epoch the listing was posted. */
  listedAt: bigint;
}

export interface AtlasState {
  conn: DbConnection | null;
  /** Hex of the local caller identity. */
  identityHex: string | null;
  /** True once an Account row exists for this identity. */
  hasAccount: boolean;
  username: string | null;
  displayName: string | null;
  /** The local player's avatar (null until the avatar row resolves). */
  localAvatar: LocalAvatar | null;
  /** Micros-since-epoch of the last display-name change (0 = never). */
  displayNameLastChangedMicros: bigint;

  /** Resolved live Commons world id (auto-increment, resolved at runtime). */
  commonsWorldId: bigint | null;
  /** The local player's owned personal world id. */
  personalWorldId: bigint | null;
  /** The world the local avatar is currently in. */
  currentWorldId: bigint | null;

  /** Local predicted transform. */
  localX: number;
  localY: number;
  localZ: number;
  localYaw: number;
  /** Camera pitch (rad, + = up) — the input module writes it; the avatar module
   *  applies it plus the feel offsets every frame (combat wave). */
  localPitch: number;
  /** Movement mode intent: 0 walk · 1 sprint · 2 slide (combat_core MoveMode). */
  intentMode: number;
  /** COMBAT WAVE: a slide TAP, latched until the avatar's next fixed step consumes it (the tap is the commitment; a frame hitch must not lose it).
   *  LEGACY writer (probes, XR): the avatar module converts it into an `intents` request. */
  slideRequested: boolean;
  /** BUILD-NOW wave 1 (2026-09-12): the ONE buffered-request queue for
   *  discrete actions (jump / slide / crouch / dash / mantle / parry) — TTLs
   *  in fixed ticks, consumed at most once by the avatar's fixed step, every
   *  outcome logged (`__intents()`). lib/intent_queue.ts. */
  intents: IntentQueue;
  /** The avatar's fixed-step counter (requests are stamped with it). */
  fixedTick: number;
  /** Guard held (right mouse, on foot) — the avatar module mirrors it to the
   *  server (`setGuard`); a raised guard walks, never sprints, slides or dashes. */
  guardHeld: boolean;
  /** Monotonic input sequence sent to the `move` reducer. */
  inputSeq: number;
  /** True while the boot loading screen gates exploration (movement intent is
   *  ignored until terrain/water/sky/foliage/structures report ready). */
  loadingGate: boolean;

  /** True while the pilot flies the space sector — SpaceModule owns the camera,
   *  the body transform, and position sends (spaceMove); the avatar's walking
   *  control/physics/move-sends stand down. */
  spaceFlight: boolean;
  /** Walking a station / ship-deck interior while the space sector stays
   *  active (SpaceModule.toggleInterior) — the ground-style menu bar applies. */
  spaceInterior: boolean;
  /** The enterable-building id the player is INSIDE (BuildingInteriorModule);
   *  null outdoors. The versona life layer shows a home's residents only to
   *  a player standing in that home. */
  interiorId: string | null;
  /** The cave the player is inside (terrain wave 2026-09-08) — the terrain clamp yields to the cave's trimesh, like a building interior. */
  caveId: string | null;
  /** Camera is under water (sea or lake) — set by AtlasWaterModule. */
  submerged: boolean;
  /** Space combat is live (pirates engaging / recent hits) — set by SpaceModule. */
  combatSpace: boolean;

  /** Per-frame movement intent set by the input module, consumed by FixedSim. */
  intentX: number;
  intentZ: number;
  intentJump: boolean;

  /** partId (string key) currently held by the personal build gizmo — Freeform
   *  rendering skips replicated-transform re-apply for this ONE mesh so the row
   *  echo of our own commit can't snap it mid-drag. */
  gizmoHeldPartId: string | null;
  /** An immersive WebXR session is presenting — the headset owns the camera pose;
   *  desktop writers (mouse pitch, keyboard intent) stand down. */
  xrPresenting: boolean;
  /**
   * Deferred world boot (two-phase homepage, 2026-08-03): main.ts assigns an
   * idempotent loader that App.useLate()s every world module. First fired on
   * atlas:world-picked (boot overlaps traveler select); AtlasNetworkModule
   * awaits it before any world entry. Null on harness/embed pages.
   */
  ensureWorldBoot: (() => Promise<void>) | null;
  /** Admin creative fly camera active — parks the avatar + frees the camera rig. */
  flyMode: boolean;
  /** The unified Edit Mode (F2) is active — the editor owns camera, cursor,
   *  selection and undo; legacy editors (admin fly/palette, Build Mode v2,
   *  Forge Canvas) stand down behind this flag. */
  editMode: boolean;
  /** Riding a crafted hovercraft — AtlasVehicleModule owns position + sends
   *  (vehicleMove); the walking KCC/move-sends stand down like spaceFlight. */
  hoverRide: boolean;
  /** Mouse look delta (radians, + = right) accumulated while RIDING, consumed
   *  and zeroed by the vehicle's fixed tick. The mouse steers the wheel, not the
   *  hull: writing localYaw directly bypassed the whole steering model. */
  hoverMouseDx: number;
  /** A window has the gamepad: the UI navigator is driving focus with it, so the
   *  world must not also read it (a d-pad press would both move the focus and
   *  swing the weapon). */
  padUiCapture: boolean;
  /** On-foot camera distance behind the eye (0 = first person; ≥ FIRST_PERSON_MAX = third person; the scroll wheel). */
  camBack: number;
  /** Hovercraft chase distance (the scroll wheel while riding). */
  hoverCamBack: number;
  /** Flying the ship in the planet's atmosphere — AtlasVehicleModule owns the
   *  rig + position sends (flyMove) until landing or the ascent to space. */
  skyFlight: boolean;
  /**
   * True while an admin placement is selected for transform editing.
   * Suppresses fly-movement and canvas pointer-lock re-request so the
   * TransformControls gizmo and numeric panel can receive pointer/keyboard events.
   */
  editorCursorMode: boolean;

  /** All asset rows the client knows about, keyed by assetId string (global table). */
  assets: Map<string, Asset>;
  /** The local player's inventory items, keyed by itemId string. */
  inventory: Map<string, InventoryItem>;

  /** The local player's hard-currency (Sovereigns) balance (base units). */
  walletSov: bigint;
  /** The local player's soft-currency (Influence) balance (base units). */
  walletInf: bigint;
  /** Active marketplace listings keyed by listingId string (global market table). */
  listings: Map<string, MarketListing>;

  /** Remote avatars keyed by identity hex. */
  remotes: Map<string, RemoteAvatar>;
  /** Placed objects in the current world keyed by object id. */
  worldObjects: Map<string, PlacedObject>;
  /** Render proxy for the local avatar. */
  localMesh: THREE.Object3D | null;

  // ─── Social Pulse / Compass / Orbits (P6) ──────────────────────────────────
  // The client only ever MIRRORS these authoritative rows; every gauge, cadence
  // check, Wave grant, and Orbit decision is the server's. A low or empty need is
  // NEVER punitive — it is only an invitation to a venue.

  /** The local player's four-need Pulse (null until the row resolves). */
  localPulse: PulseState | null;
  /** Every public Compass the client can see, keyed by owner identity hex. */
  compasses: Map<string, CompassState>;
  /** All known Orbits keyed by orbitId string. */
  orbits: Map<string, OrbitState>;
  /** All known Orbit memberships keyed by memberId string. */
  orbitMembers: Map<string, OrbitMemberState>;
  /** Declared venue need-satisfiers keyed by worldId string. */
  needSatisfiers: Map<string, NeedSatisfier>;

  // ─── Reputation & Trust (P7) ────────────────────────────────────────────────
  // "Relationships, not stats." Four explicit scores make standing legible; the
  // vouch graph turns trust into a staked, public relationship; reviews and
  // betrayals are the consequences that make the graph mean something. The client
  // only ever MIRRORS these rows — every score is server-computed (rollups hourly,
  // penalties on the betrayal event) and there is no client reducer to write one.
  // The UI previews vouch weight / collab eligibility with the shared pure math,
  // but the server is always the authority.

  /** The local player's four reputation scores (null until the row resolves). */
  localReputation: ReputationScores | null;
  /** Every reputation row the client can see, keyed by identity hex. */
  reputations: Map<string, ReputationScores>;
  /** Every known vouch edge (active and forfeited), keyed by vouchId string. */
  vouches: Map<string, VouchEdge>;
  /** Every known review (star + Caution), keyed by reviewId string. */
  reviews: Map<string, Review>;
  /** Every known betrayal record, keyed by recordId string. */
  betrayals: Map<string, BetrayalRecord>;

  // ─── Rituals, Events & Memory Book (P8) ─────────────────────────────────────
  // "Moments, not metrics." An Event becomes a Ritual once it has convened enough
  // times, gains a DETERMINISTIC NPC host that remembers its regulars, and earns
  // the right to send Standing Invitations. The Memory Book is the public archive
  // of lineage, photos, replays, milestones, and read-only ghost archives. The
  // client only ever MIRRORS these rows — every count, payout, and host memory is
  // server-computed; there is no client reducer to write one.

  /** Every scheduled event the client can see, keyed by eventId string. */
  events: Map<string, EventSpec>;
  /** Every attendance row the client can see, keyed by attendanceId string. */
  attendances: Map<string, EventAttendance>;
  /** Every ritual row (1:1 with a recurring event), keyed by ritualId string. */
  rituals: Map<string, RitualState>;
  /** Per-(ritual, identity) Standing-Invitation opt-outs, keyed by muteId string. */
  ritualMutes: Map<string, RitualMute>;
  /** Deterministic NPC hosts bound to institutionalized rituals, keyed by npcId string. */
  npcHosts: Map<string, NpcHost>;
  /** Every Memory Book entry the client can see, keyed by entryId string. */
  memoryEntries: Map<string, MemoryEntry>;

  // ─── Groups, Charters, Treasuries & Forks (P9) ──────────────────────────────
  // "Institutions, not labels." A Group is the guild equivalent — a player-formed
  // organization with a public Charter, a shared Treasury governed by transparent
  // spend limits and member votes, an officer role structure, embezzlement
  // penalties with mechanical teeth, and fork/schism mechanics. The client only
  // ever MIRRORS these rows: the formation fee burns through the P3 ledger, the
  // Treasury balance MIRRORS the ledger, spend approval is the authoritative vote
  // tally, the embezzlement penalty cleaves a P7 score, and a fork's proportional
  // split is computed at resolution. There is no client reducer to write one.

  /** Every group the client can see, keyed by groupId string. */
  groups: Map<string, Group>;
  /** Every group membership row, keyed by memberId string. */
  groupMembers: Map<string, GroupMember>;
  /** Every charter signature, keyed by signatureId string. */
  groupSignatures: Map<string, GroupSignature>;
  /** Every standing charter challenge, keyed by challengeId string. */
  charterChallenges: Map<string, CharterChallenge>;
  /** Every Treasury spend proposal, keyed by spendId string. */
  treasurySpends: Map<string, TreasurySpend>;
  /** Every spend ballot, keyed by voteId string. */
  spendVotes: Map<string, SpendVote>;
  /** Every open/settled fork, keyed by forkId string. */
  forkVotes: Map<string, ForkVote>;
  /** Every fork ballot, keyed by ballotId string. */
  forkBallots: Map<string, ForkBallot>;
}

/** The single shared Atlas state instance. */
export const atlas: AtlasState = {
  conn: null,
  identityHex: null,
  hasAccount: false,
  username: null,
  displayName: null,
  localAvatar: null,
  displayNameLastChangedMicros: 0n,
  commonsWorldId: null,
  personalWorldId: null,
  currentWorldId: null,
  localX: 0,
  localY: 1,
  localZ: 0,
  localYaw: 0,
  localPitch: 0,
  intentMode: 0,
  slideRequested: false,
  intents: new IntentQueue(),
  fixedTick: 0,
  guardHeld: false,
  inputSeq: 0,
  loadingGate: true,
  spaceFlight: false,
  spaceInterior: false,
  interiorId: null,
  caveId: null,
  submerged: false,
  combatSpace: false,
  intentX: 0,
  intentZ: 0,
  intentJump: false,
  gizmoHeldPartId: null,
  xrPresenting: false,
  ensureWorldBoot: null,
  flyMode: false,
  hoverRide: false,
  hoverMouseDx: 0,
  padUiCapture: false,
  camBack: 0,
  hoverCamBack: 8.5,
  skyFlight: false,
  editMode: false,
  editorCursorMode: false,
  assets: new Map(),
  inventory: new Map(),
  walletSov: 0n,
  walletInf: 0n,
  listings: new Map(),
  remotes: new Map(),
  worldObjects: new Map(),
  localMesh: null,
  localPulse: null,
  compasses: new Map(),
  orbits: new Map(),
  orbitMembers: new Map(),
  needSatisfiers: new Map(),
  localReputation: null,
  reputations: new Map(),
  vouches: new Map(),
  reviews: new Map(),
  betrayals: new Map(),
  events: new Map(),
  attendances: new Map(),
  rituals: new Map(),
  ritualMutes: new Map(),
  npcHosts: new Map(),
  memoryEntries: new Map(),
  groups: new Map(),
  groupMembers: new Map(),
  groupSignatures: new Map(),
  charterChallenges: new Map(),
  treasurySpends: new Map(),
  spendVotes: new Map(),
  forkVotes: new Map(),
  forkBallots: new Map(),
};
